import React, { useState, useEffect } from 'react';

const CalendarWidget = ({
  startDate,
  endDate,
  onSelectDays,
  selectedSubtask,
  initialPlannedDates = [], // These are the days set in frequency.days
  initialCompletedDates = [], // These are the days marked as done
}) => {
  // Track which days have been completed
  const [completedDates, setCompletedDates] = useState([]);

  // Update completedDays when initialCompletedDays changes
  useEffect(() => {
    if (initialCompletedDates?.length > 0) {
      const formattedDays = initialCompletedDates.map(
        (date) => new Date(date).toISOString().split('T')[0],
      );
      setCompletedDates(formattedDays);
    } else {
      setCompletedDates([]);
    }
  }, [initialCompletedDates]);

  const handleDayToggle = (day) => {
    // Get planned days in consistent format
    const plannedDays =
      selectedSubtask?.frequency?.days?.map(
        (date) => new Date(date).toISOString().split('T')[0],
      ) || [];

    // For custom frequency, only allow toggling of planned days
    if (selectedSubtask?.frequency?.type === 'custom') {
      const isPlannedDay = plannedDays.includes(day);
      if (!isPlannedDay) {
        return; // Don't allow toggling if the day wasn't planned
      }
    }

    // Toggle completion status
    const newCompletedDates = completedDates.includes(day)
      ? completedDates.filter((d) => d !== day)
      : [...completedDates, day];

    setCompletedDates(newCompletedDates);
    onSelectDays(newCompletedDates.map((date) => new Date(date)));
  };

  const generateDays = () => {
    const days = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d).toISOString().split('T')[0]);
    }
    return days;
  };

  const allDays = generateDays();

  // Get planned days in consistent format
  const plannedDates =
    selectedSubtask?.frequency?.days?.map(
      (date) => new Date(date).toISOString().split('T')[0],
    ) || [];

  return (
    <div className="w-[300px] bg-white rounded-lg shadow-md">
      <div className="p-3 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-800 truncate">
          {selectedSubtask?.subTaskName || 'Select Days'}
        </h3>
      </div>

      <div className="grid grid-cols-7 gap-1 p-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
          <div key={index} className="text-center text-xs text-gray-500 py-1">
            {day}
          </div>
        ))}

        {[...Array(new Date(startDate).getDay())].map((_, index) => (
          <div key={`empty-start-${index}`} className="aspect-square" />
        ))}

        {allDays.map((day) => {
          const date = new Date(day);
          const dayOfMonth = date.getDate();
          const isCompleted = completedDates.includes(day);
          const isToday = new Date().toISOString().split('T')[0] === day;

          // Check if this day was planned (in frequency.days)
          const isPlannedDate =
            selectedSubtask?.frequency?.type === 'custom' &&
            plannedDates.includes(day);

          // A day is clickable if it's either:
          // 1. Not a custom frequency type, OR
          // 2. A custom frequency type AND was planned
          const isClickable =
            selectedSubtask?.frequency?.type !== 'custom' ||
            (selectedSubtask?.frequency?.type === 'custom' && isPlannedDate);

          return (
            <div
              key={day}
              onClick={() => isClickable && handleDayToggle(day)}
              className={`
                aspect-square
                flex
                items-center
                justify-center
                text-xs
                rounded-full
                ${
                  isClickable
                    ? 'cursor-pointer'
                    : 'cursor-not-allowed opacity-50'
                }
                transition-colors
                ${
                  isCompleted
                    ? 'bg-green-600 text-white' // Completed days in green
                    : isPlannedDate
                    ? 'bg-gray-200' // Planned days in gray
                    : 'hover:bg-gray-100 text-gray-700'
                }
                ${isToday ? 'ring-1 ring-indigo-400' : ''}
              `}
            >
              {dayOfMonth}
            </div>
          );
        })}

        {[...Array(6 - new Date(endDate).getDay())].map((_, index) => (
          <div key={`empty-end-${index}`} className="aspect-square" />
        ))}
      </div>

      <div className="p-3 border-t border-gray-200">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gray-200 rounded-full mr-1"></div>
            <span>Planned</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-600 rounded-full mr-1"></div>
            <span>Completed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;
